/**
 * recaptcha.ts — frontend helper for reCAPTCHA Enterprise (invisible v3-style).
 *
 * The site key is public and configured at build time via the Vite env var
 * `VITE_RECAPTCHA_SITE_KEY`. The script is loaded once and cached on
 * `window.grecaptcha.enterprise`.
 *
 * Usage:
 *   const token = await getRecaptchaToken("contact");
 *   // POST { ...form, recaptchaToken: token } to /api/contact
 *
 * If the site key is not configured (e.g. local dev before secrets are set),
 * `getRecaptchaToken` resolves to `undefined` so the form still submits — the
 * Cloud Function logs the missing-config case loudly and rejects in
 * production where the secrets must be set.
 */

declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (cb: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

const SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY as string | undefined;

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (!SITE_KEY) return Promise.reject(new Error("recaptcha.no_site_key"));
  if (typeof window === "undefined") return Promise.reject(new Error("recaptcha.no_window"));
  if (window.grecaptcha?.enterprise) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const url = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(SITE_KEY)}`;
    const existing = document.querySelector<HTMLScriptElement>(`script[src^="https://www.google.com/recaptcha/enterprise.js"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("recaptcha.script_error")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = url;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener("error", () => reject(new Error("recaptcha.script_error")), { once: true });
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Execute a reCAPTCHA Enterprise assessment for the given action and return
 * a token to include in the POST body. Returns `undefined` on any failure
 * (no site key, script load error, grecaptcha unavailable) — the Cloud
 * Function decides how to handle missing tokens.
 */
export async function getRecaptchaToken(action: string): Promise<string | undefined> {
  if (!SITE_KEY) {
    if (import.meta.env.DEV) {
      // In dev without a key, allow submissions through.
      // eslint-disable-next-line no-console
      console.info("[recaptcha] no VITE_RECAPTCHA_SITE_KEY — skipping");
    }
    return undefined;
  }
  try {
    await loadScript();
    const enterprise = window.grecaptcha?.enterprise;
    if (!enterprise) return undefined;
    await new Promise<void>((resolve) => enterprise.ready(resolve));
    return await enterprise.execute(SITE_KEY, { action });
  } catch {
    return undefined;
  }
}
